import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Divider, IconButton, Grid, DialogTitle, Stack, Theme, useTheme, styled } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { ShowDialogProps } from 'src/show-dialog'
import {
  FoulingType,
  FoulingAmount,
  WashingReportDto,
  WashingReportAttachment,
} from 'src/generated-types/washing-report-dto'
import { IFile } from 'src/generated-types/file'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { isFileImage, getFormattedDate, getThemeColor } from 'src/utils'
import { FileUrlLoader } from 'src/components/common/input/CommonFileUploader'
import { MUIThemeColors } from 'src/mui-theme'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'
import { Spinner } from 'src/components/Spinner'
import FileViewer from 'react-file-viewer'
import { Flex } from 'src/styles/flexComponent'
import { Close } from '@mui/icons-material'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { GridActionButton } from 'src/components/common/GridActionButton'

interface WashingReportDetailsDialogProps extends ShowDialogProps {
  washingReport: WashingReportDto
}

interface ReadOnlyFieldProps {
  label: string
  value: React.ReactNode
}

export const WashingReportDetailsDialog: React.FC<WashingReportDetailsDialogProps> = ({ pop, washingReport }) => {
  const { t } = useTranslation(['equipment', 'common', 'washing-report'])
  const theme = useTheme()
  const [selectedAttachment, setSelectedAttachment] = useState<WashingReportAttachment | null>(
    washingReport?.washingReportAttachments?.[0] ?? null,
  )

  const hasAttachments = useMemo(
    () => (washingReport?.washingReportAttachments?.length ?? 0) > 0,
    [washingReport?.washingReportAttachments],
  )

  const formatBooleanValue = (value: boolean | undefined) => {
    if (value === undefined || value === null) return '-'
    return value ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' })
  }

  const formatWashedOn = (dateString: string | undefined) => {
    if (!dateString || dateString.startsWith('0001-01-01')) return '-'
    return getFormattedDate(new Date(dateString))
  }

  return (
    <GenericDialogLayout
      pop={pop}
      renderCustomHeader={pop => (
        <HeaderContainer theme={theme}>
          <StyledDialogTitle>
            <HeaderStack>
              <HeaderTitle theme={theme}>{t('washing-report-details', { ns: 'washing-report' })}</HeaderTitle>
              <Flex.Row alignItems="center" gap={1}>
                {pop && (
                  <CloseIconButton aria-label="close" onClick={pop} theme={theme}>
                    <Close />
                  </CloseIconButton>
                )}
              </Flex.Row>
            </HeaderStack>
          </StyledDialogTitle>
        </HeaderContainer>
      )}
      sx={{ padding: 3 }}
    >
      <Container theme={theme}>
        <DetailsContainer>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 2, color: getThemeColor(theme, MUIThemeColors.primaryMain) }}
          >
            {t('details', { ns: 'common' })}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <ReadOnlyField
                label={t('fouling-type', { ns: 'equipment' })}
                value={
                  washingReport?.foulingType !== undefined
                    ? t(FoulingType[washingReport.foulingType], { ns: 'equipment' })
                    : '-'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ReadOnlyField
                label={t('fouling-amount', { ns: 'equipment' })}
                value={
                  washingReport?.foulingAmount !== undefined
                    ? t(FoulingAmount[washingReport.foulingAmount], { ns: 'equipment' })
                    : '-'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ReadOnlyField label={t('pressure', { ns: 'equipment' })} value={washingReport?.pressure ?? '-'} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ReadOnlyField
                label={t('time-consumption', { ns: 'equipment' })}
                value={washingReport?.timeConsumption ?? '-'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ReadOnlyField
                label={t('washed-on', { ns: 'equipment' })}
                value={formatWashedOn(washingReport?.washedOn)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ReadOnlyField
                label={t('inspection', { ns: 'equipment' })}
                value={formatBooleanValue(washingReport?.inspection)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ReadOnlyField
                label={t('extra-time-consumption', { ns: 'equipment' })}
                value={formatBooleanValue(washingReport?.extraTimeConsumption)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ReadOnlyField
                label={t('createdBy', { ns: 'common' })}
                value={
                  washingReport?.createdByUser ? getUserDetail(washingReport.createdByUser, UserDetail.FullName) : '-'
                }
              />
            </Grid>
            {washingReport?.notes && (
              <Grid item xs={12}>
                <ReadOnlyField label={t('notes', { ns: 'equipment' })} value={washingReport.notes} />
              </Grid>
            )}
          </Grid>
        </DetailsContainer>

        <Divider />

        <AttachmentContainer>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ mb: 2, color: getThemeColor(theme, MUIThemeColors.primaryMain) }}
          >
            {t('attachments', { ns: 'common' })}
          </Typography>{' '}
          {hasAttachments ? (
            <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
              <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <AttachmentListScrollBox>
                  {washingReport.washingReportAttachments?.map(attachment => (
                    <AttachmentListItem
                      key={attachment.id}
                      attachment={attachment}
                      isSelected={selectedAttachment?.id === attachment.id}
                      onSelect={() => setSelectedAttachment(attachment)}
                    />
                  ))}
                </AttachmentListScrollBox>
              </Grid>
              <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column', minHeight: '250px' }}>
                {selectedAttachment?.file && <FilePreviewPanel file={selectedAttachment.file} />}
              </Grid>
            </Grid>
          ) : (
            <EmptyStatusCard
              imageWidth="96px"
              header={t('no-attachments-found', { ns: 'common' })}
              headerFontSize="18px"
              contentFontSize="14px"
              contentClasses="mt-2"
              contentTopMargin="10px"
              imageContainerClasses="pb-4 pt-4"
              height="200px"
            />
          )}
        </AttachmentContainer>
      </Container>
    </GenericDialogLayout>
  )
}

export default WashingReportDetailsDialog

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => {
  const theme = useTheme()

  if (value === null || value === undefined || value === '') return null

  return (
    <Box sx={{ mb: 0 }}>
      <Typography
        variant="body2"
        color={getThemeColor(theme, MUIThemeColors.secondaryDark)}
        sx={{ display: 'block', fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  )
}

interface AttachmentListItemProps {
  attachment: WashingReportAttachment
  isSelected: boolean
  onSelect: () => void
}

const getFileTypeLabel = (contentType: string): string => {
  const type = contentType.toLowerCase()
  if (type.includes('word') || type.includes('vnd.ms-word') || type === 'application/msword') {
    return 'WORD'
  }
  if (type.includes('excel') || type.includes('spreadsheet') || type === 'application/vnd.ms-excel') {
    return 'EXCEL'
  }
  if (type.includes('powerpoint') || type.includes('presentation')) {
    return 'POWERPOINT'
  }
  if (type.includes('pdf')) {
    return 'PDF'
  }
  return contentType.split('/')[1]?.toUpperCase() || ''
}

const AttachmentListItem: React.FC<AttachmentListItemProps> = ({ attachment, isSelected, onSelect }) => {
  const theme = useTheme()
  const file = attachment.file

  const isImage = file?.contentType?.includes('image')
  const shortenedName = file?.filename?.length > 30 ? file.filename.substring(0, 27) + '...' : file?.filename

  return (
    <StyledAttachmentListItem onClick={onSelect} isSelected={isSelected} theme={theme}>
      {isImage ? (
        <AttachmentThumbnail isImage>
          <img
            src={file?.url}
            alt={file?.filename}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </AttachmentThumbnail>
      ) : (
        <AttachmentThumbnail>
          <FileUrlLoader
            contentTypeValue={file?.contentType}
            fileUrl={file?.url}
            openOnClick={false}
            className="text-[24px] h-[24px] w-[24px]"
          />
        </AttachmentThumbnail>
      )}
      <Box sx={{ px: 1.5, py: 0.5, minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.85rem',
          }}
          title={file?.filename}
        >
          {shortenedName}
        </Typography>
        {file?.contentType && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {getFileTypeLabel(file.contentType)}
          </Typography>
        )}
      </Box>
    </StyledAttachmentListItem>
  )
}

interface FilePreviewPanelProps {
  file: IFile
}

const FilePreviewPanel: React.FC<FilePreviewPanelProps> = ({ file }) => {
  const theme = useTheme()
  const { t } = useTranslation(['common'])
  const { openImagePreviewDialog, openFilePreviewDialog } = useDialogCtx()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [blobUrl, setBlobUrl] = useState<string>('')

  const isImage = useMemo(() => isFileImage(file), [file])
  const fileType = useMemo(() => file?.url?.split('?')[0].split('.').pop()?.toLowerCase() || '', [file?.url])

  useEffect(() => {
    let objectUrl = ''

    const fetchFile = async () => {
      try {
        setLoading(true)
        setError(null)
        setBlobUrl('')
        const response = await fetch(file.url, { mode: 'cors' })
        if (!response.ok) throw new Error('Failed to fetch file')

        const blob = await response.blob()
        objectUrl = URL.createObjectURL(blob)
        setBlobUrl(objectUrl)
        setLoading(false)
      } catch (e) {
        setError((e as Error)?.message || 'Error loading file')
        setLoading(false)
      }
    }

    fetchFile()

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [file.url])

  return (
    <FilePreviewContainerBox theme={theme}>
      <MaximizeOverlayButton
        onClick={() => {
          if (isImage) {
            openImagePreviewDialog(file.url, file?.filename, file?.filename)
          } else {
            openFilePreviewDialog(file.url, file?.filename)
          }
        }}
        theme={theme}
      >
        <GridActionButton
          buttonIcon={<OpenInNewIcon />}
          size="small"
          toolTipTitle={t('maximize', { ns: 'common' }) as string}
          width="36px"
        />
      </MaximizeOverlayButton>

      <FilePreviewContentBox theme={theme}>
        {loading && !error && <Spinner />}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
        {!loading && !error && blobUrl && isImage && (
          <img
            src={blobUrl}
            alt={file?.filename}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        )}
        {!loading && !error && blobUrl && !isImage && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            <FileViewer
              fileType={fileType}
              filePath={blobUrl}
              onError={(e: Error) => {
                setError(e?.message || 'Error loading file')
              }}
            />
          </Box>
        )}
      </FilePreviewContentBox>
    </FilePreviewContainerBox>
  )
}

const HeaderContainer = styled(Box)<{ theme: Theme }>(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  height: '50px',
}))

const StyledDialogTitle = styled(DialogTitle)({
  padding: '12px 16px 0 16px',
})

const HeaderStack = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '-5px',
})

const HeaderTitle = styled(Typography)<{ theme: Theme }>(({ theme }) => ({
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: 700,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

const CloseIconButton = styled(IconButton)<{ theme: Theme }>(({ theme }) => ({
  '& svg': {
    color: getThemeColor(theme, MUIThemeColors.secondaryMain),
    fontSize: '18px',
  },
}))

const Container = styled(Box)(() => ({
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
}))

const DetailsContainer = styled(Box)(() => ({
  padding: '15px',
}))

const AttachmentContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '15px',
  minHeight: 0,
}))

const StyledAttachmentListItem = styled(Box)<{ isSelected: boolean; theme: any }>(({ theme, isSelected }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  height: 45,
  border: '1px solid',
  borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
  borderRadius: '4px',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.2s',
  marginBottom: '5px',
  marginRight: '5px',
  backgroundColor: isSelected
    ? getThemeColor(theme, MUIThemeColors.backgroundMain)
    : getThemeColor(theme, MUIThemeColors.white),
  '&:hover': {
    boxShadow: theme.shadows[2],
    borderColor: theme.palette.primary.main,
  },
}))

const FilePreviewContainerBox = styled(Box)<{ theme: any }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  borderRadius: '4px',
  height: '400px',
  position: 'relative',
  overflow: 'hidden',
}))

const FilePreviewContentBox = styled(Box)<{ theme: any }>(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  overflow: 'hidden',
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  padding: theme.spacing(1),
}))

const MaximizeOverlayButton = styled(Box)<{ theme: any }>(() => ({
  position: 'absolute',
  top: 4,
  right: 4,
  zIndex: 2,
  padding: 4,
}))

const AttachmentListScrollBox = styled(Box)(() => ({
  maxHeight: '400px',
  overflowY: 'auto',
  pr: 1,
}))

const AttachmentThumbnail = styled(Box)<{ isImage?: boolean }>(({ theme, isImage }) => ({
  width: 60,
  alignSelf: 'stretch',
  flexShrink: 0,
  backgroundColor: isImage ? theme.palette.grey[100] : theme.palette.grey[50],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}))
